import { useState, useMemo, useCallback, useEffect } from "react";
import { useTerminology } from "@/contexts/TerminologyContext";
import { useTranslation } from "react-i18next";
import { Search, CalendarIcon, Plus, Loader2, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { companies as dummyCompanies, people } from "@/data/dummy";
import { usePipelines } from "@/lib/hooks/use-pipelines";
import CompanyLogo from "@/components/shared/CompanyLogo";
import UserAvatar from "@/components/shared/Avatar";
import { useDealFields } from "@/lib/hooks/use-deal-fields";
import { useDealMutations } from "@/lib/hooks/use-deal-mutations";
import { useWorkspaceMembers } from "@/lib/hooks/use-workspace-members";
import { useSearchCompaniesLazyQuery } from "@/lib/graphql/types-and-hooks";
import type { PipelineStage } from "@/data/pipelines";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { isLovablePreview } from "@/lib/utils/environment";
import { Skeleton } from "@/components/ui/skeleton";
import AddCompanyForm from "@/components/shared/AddCompanyForm";
import { useCurrentUser, getUserFullName } from "@/lib/hooks/use-current-user";
import DealProductsPicker from "@/components/shared/DealProductsPicker";
import {
  type DealLineItem,
  lineItemsTotal,
  useDealProductsMap,
} from "@/hooks/useDealProducts";
import { useSetDealLineItemsMutation } from "@/lib/graphql/types-and-hooks";
import { useFeatureFlag, FEATURE_FLAGS } from "@/lib/feature-flags";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useStageLabel, usePipelineLabel } from "@/lib/i18n/stage-label";
import { displayDate } from "@/lib/i18n/display";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd?: (deal: { company: string; name: string; amount: number; stage: string; closeDate: string; owner: string; customFields?: Record<string, string>; lineItems?: DealLineItem[]; backendId?: string; backendDeal?: unknown; pipelineId?: string; stageName?: string }) => void;
  /**
   * Optional initial values applied when the dialog opens. Used by Ask Coffee
   * to prefill a draft deal extracted from a natural-language request.
   * Closing the dialog clears these values via the parent.
   */
  prefill?: {
    company?: string;
    /** Account id behind `company`. Required for the association to be saved —
     *  createDeal takes accountIds, so a name alone creates a company-less deal. */
    companyId?: string;
    companyDomain?: string;
    name?: string;
    amount?: number;
    stage?: string;
    closeDate?: string; // ISO YYYY-MM-DD
    owner?: string;
    customFields?: Record<string, string>;
    lineItems?: DealLineItem[];
  };
  /** Pipeline to preselect when the dialog opens (e.g. the Deals page's active
   *  pipeline filter). Falls back to the primary pipeline when omitted. */
  initialPipelineId?: string;
  /** When "clone", the sheet title/submit button read "Clone …" instead of "Add …". */
  mode?: "add" | "clone";
}

function getEndOfQuarter(): Date {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3);
  return new Date(now.getFullYear(), (quarter + 1) * 3, 0);
}

const BUILT_IN_KEYS = new Set(['company', 'name', 'amount', 'stage', 'close_date', 'owner']);

/**
 * date-fns pattern, not copy. Hoisted out of JSX so the i18n lint rule does not
 * read the token string as user-facing text; the app-wide move onto the shared
 * locale-aware formatters is tracked separately.
 */
const DATE_PATTERN = "MMM d, yyyy";

/** Returns the value to store/submit for a stage: externalId (production) or name (preview fallback) */
function getStageValue(s: PipelineStage | undefined): string {
  if (!s) return "";
  return s.externalId || s.name;
}

const dummyTeamMembers = [
  { id: "dummy-1", name: "Doug" },
  { id: "dummy-2", name: "Lisa" },
  { id: "dummy-3", name: "Mike" },
  ...people.map(p => ({ id: `dummy-${p.name}`, name: p.name })),
];
const uniqueDummyOwners = Array.from(new Map(dummyTeamMembers.map(m => [m.name, m])).values());

export default function AddDealDialog({ open, onOpenChange, onAdd, prefill, initialPipelineId, mode = "add" }: Props) {
  const stageLabel = useStageLabel();
  const pipelineLabel = usePipelineLabel();
  const { terminology, singular } = useTerminology();
  const { t } = useTranslation();
  const { symbol: currencySymbol } = useCurrency();
  const { fields, loading: fieldsLoading, error: fieldsError, getFieldLabel, refetch: refetchDealFields, setPipelineId: setDealFieldsPipelineId } = useDealFields();
  const { pipelines } = usePipelines();
  const { createDeal, creating } = useDealMutations();
  const workspaceId = useWorkspaceId();
  const { members: workspaceMembers } = useWorkspaceMembers();
  const preview = isLovablePreview();

  const { currentUser } = useCurrentUser();

  // Server-side company search for production mode
  const [searchCompaniesQuery, { loading: companiesLoading }] = useSearchCompaniesLazyQuery();
  const [companyResults, setCompanyResults] = useState<Array<{ id: string; name: string; domain: string }>>([]);

  const hasMultiplePipelines = pipelines.length > 1;
  const defaultPipeline = pipelines[0];
  const defaultStages = defaultPipeline?.stages || [];
  const primaryPipelineId = defaultPipeline?.id;
  // Pipeline to preselect on open: the one passed by the caller (e.g. the Deals
  // page's active filter) when it resolves to a real pipeline, else the primary.
  const initialPipeline =
    pipelines.find(p => p.id === initialPipelineId) || defaultPipeline;

  const [company, setCompany] = useState("");
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companyDomain, setCompanyDomain] = useState("");
  const [companyConfirmed, setCompanyConfirmed] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [showAddCompanyForm, setShowAddCompanyForm] = useState(false);
  const [dealName, setDealName] = useState("");
  /** True once the user manually edits the deal name — stops auto-syncing from company. */
  const [dealNameTouched, setDealNameTouched] = useState(false);
  const [amount, setAmount] = useState("");
  const [selectedPipelineId, setSelectedPipelineId] = useState(initialPipeline?.id || "");
  const [showPipelineDropdown, setShowPipelineDropdown] = useState(false);
  const [stage, setStage] = useState(getStageValue((initialPipeline?.stages || [])[0]));
  const [closeDate, setCloseDate] = useState<Date>(getEndOfQuarter());
  const [owner, setOwner] = useState(getUserFullName(currentUser));
  const [ownerId, setOwnerId] = useState<string | null>(currentUser?.id ?? null);
  const [ownerSearch, setOwnerSearch] = useState("");
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
  const [showStageDropdown, setShowStageDropdown] = useState(false);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});
  const productsEnabled = useFeatureFlag(FEATURE_FLAGS.PRODUCTS);
  const { setForDeal: setProductsForDeal } = useDealProductsMap();
  const [setDealLineItemsMutation] = useSetDealLineItemsMutation();
  const [lineItems, setLineItems] = useState<DealLineItem[]>([]);
  /** True once the user has manually edited Deal Size — stops auto-syncing from product total. */
  const [amountOverridden, setAmountOverridden] = useState(false);

  // Scope the deal-fields context to the selected pipeline so the form shows
  // that pipeline's team-wide field config. Primary pipeline → workspace
  // default (null); uncustomized pipelines inherit the default server-side.
  // Only scope while the dialog is open; reset to the workspace default on
  // close so the shared DealFieldsContext doesn't stay pinned to this
  // pipeline for other consumers (e.g. Settings → New Deal Fields).
  useEffect(() => {
    if (!open) {
      setDealFieldsPipelineId(null);
      return;
    }
    setDealFieldsPipelineId(
      selectedPipelineId && selectedPipelineId !== primaryPipelineId ? selectedPipelineId : null
    );
  }, [open, selectedPipelineId, primaryPipelineId, setDealFieldsPipelineId]);

  // All fields sorted by position
  const sortedFields = useMemo(() =>
    [...fields].sort((a, b) => a.position - b.position),
    [fields]
  );

  // Debounced company search
  const fetchCompanySuggestions = useCallback(async (search: string) => {
    if (preview) {
      const filtered = dummyCompanies
        .filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()))
        .slice(0, 10)
        .map(c => ({ id: c.id || c.name, name: c.name, domain: c.website || "" }));
      setCompanyResults(filtered);
      return;
    }
    if (!search.trim() || !workspaceId) {
      setCompanyResults([]);
      return;
    }
    try {
      const result = await searchCompaniesQuery({
        variables: { workspaceId: String(workspaceId), search: search.trim(), first: 10 },
      });
      const accounts = result.data?.accounts?.edges
        ?.map(edge => edge?.node)
        .filter(Boolean)
        .map(node => ({
          id: node!.id,
          name: (node!.properties as any)?.name || node!.domain || 'Unnamed',
          domain: node!.domain || '',
        })) || [];
      setCompanyResults(accounts);
    } catch (error) {
      console.error("Error searching companies:", error);
      setCompanyResults([]);
    }
  }, [preview, workspaceId, searchCompaniesQuery]);

  useEffect(() => {
    if (!showCompanyDropdown) return;
    const timer = setTimeout(() => fetchCompanySuggestions(companySearch), 300);
    return () => clearTimeout(timer);
  }, [companySearch, showCompanyDropdown, fetchCompanySuggestions]);

  // Auto-fill deal name as "<Company> Opportunity" only when the deal name is
  // still empty at the moment a company is entered. Never overwrite an existing name.
  useEffect(() => {
    const trimmed = company.trim();
    if (!trimmed) return;
    if (dealName.trim()) return;
    setDealName(`${trimmed} Opportunity`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  // Use workspace members in production, dummy data in preview
  const ownerOptions = useMemo(() => {
    if (!preview && workspaceMembers.length > 0) {
      return workspaceMembers.map(m => ({ id: m.id, name: m.name }));
    }
    return uniqueDummyOwners;
  }, [preview, workspaceMembers]);

  const filteredOwners = useMemo(() =>
    ownerSearch.length > 0
      ? ownerOptions.filter(m => m.name.toLowerCase().includes(ownerSearch.toLowerCase()))
      : ownerOptions
  , [ownerSearch, ownerOptions]);

  const selectedPipeline = pipelines.find(p => p.id === selectedPipelineId) || defaultPipeline;
  const pipelineStages = selectedPipeline?.stages || [];
  const currentStage = pipelineStages.find(s => getStageValue(s) === stage);

  const getExtraFieldKey = useCallback((field: typeof sortedFields[0]) =>
    field.kind === 'CUSTOM' ? (field.crmFieldId || field.id || '') : (field.standardKey || field.id || ''), []);

  const reset = () => {
    setCompany(""); setCompanyId(null); setCompanyDomain(""); setCompanyConfirmed(false);
    setCompanySearch(""); setShowCompanyDropdown(false);
    setShowAddCompanyForm(false); setCompanyResults([]);
    setDealName(""); setDealNameTouched(false); setAmount("");
    setSelectedPipelineId(initialPipeline?.id || "");
    setStage(getStageValue((initialPipeline?.stages || [])[0]));
    setCloseDate(getEndOfQuarter());
    setOwner(getUserFullName(currentUser)); setOwnerId(currentUser?.id ?? null); setOwnerSearch(""); setShowOwnerDropdown(false);
    setShowStageDropdown(false); setShowPipelineDropdown(false);
    setCustomFieldValues({});
    setLineItems([]);
    setAmountOverridden(false);
  };

  // Reset form state and refetch deal fields when dialog opens to ensure fresh field config
  useEffect(() => {
    if (open) {
      reset();
      refetchDealFields();
      if (prefill) {
        if (prefill.company) {
          setCompany(prefill.company);
          // Carry the account id through, not just the display name: handleSubmit
          // sends accountIds, so a prefill without an id silently drops the
          // association and the created deal has no company (clone regression).
          if (prefill.companyId) setCompanyId(prefill.companyId);
          if (prefill.companyDomain) setCompanyDomain(prefill.companyDomain);
          setCompanyConfirmed(true);
        }
        if (prefill.name) { setDealName(prefill.name); setDealNameTouched(true); }
        if (typeof prefill.amount === "number" && !Number.isNaN(prefill.amount)) {
          setAmount(String(prefill.amount));
        }
        if (prefill.stage) {
          // Match stage name against any pipeline; fall back to default stage already set by reset().
          for (const p of pipelines) {
            const match = p.stages.find(s => s.name.toLowerCase() === prefill.stage!.toLowerCase());
            if (match) {
              setSelectedPipelineId(p.id);
              setStage(getStageValue(match));
              break;
            }
          }
        }
        if (prefill.closeDate) {
          const d = new Date(prefill.closeDate);
          if (!Number.isNaN(d.getTime())) setCloseDate(d);
        }
        if (prefill.owner) setOwner(prefill.owner);
        if (prefill.customFields && Object.keys(prefill.customFields).length > 0) {
          setCustomFieldValues({ ...prefill.customFields });
        }
        if (prefill.lineItems && prefill.lineItems.length > 0) {
          setLineItems([...prefill.lineItems]);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only trigger on open change
  }, [open]);

  const requiredExtraFieldsFilled = useMemo(() =>
    sortedFields
      .filter(f => f.required && !(f.kind === 'STANDARD' && BUILT_IN_KEYS.has(f.standardKey || '')))
      .every(f => {
        const key = getExtraFieldKey(f);
        return (customFieldValues[key] || '').trim().length > 0;
      }),
    [sortedFields, customFieldValues, getExtraFieldKey]
  );

  const canSubmit = company.trim() && dealName.trim() && requiredExtraFieldsFilled;

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!canSubmit) return;

    if (preview) {
      // Preview mode: pass data to parent via callback
      onAdd?.({
        company: company.trim(),
        name: dealName.trim(),
        amount: Number(amount.replace(/[^0-9.]/g, "")) || 0,
        stage,
        // NOT localized: this is the row VALUE, re-parsed for sorting and the
        // detail sheet. Only the trigger label below renders in the reader's locale.
        closeDate: format(closeDate, DATE_PATTERN),
        owner,
        customFields: Object.keys(customFieldValues).length > 0 ? customFieldValues : undefined,
        lineItems: lineItems.length > 0 ? lineItems : undefined,
        pipelineId: selectedPipeline?.id,
        stageName: currentStage?.name || stage,
      });
      reset();
      onOpenChange(false);
      return;
    }

    // Production mode: save to backend
    setSubmitting(true);
    try {
      const result = await createDeal({
        properties: {
          name: dealName.trim(),
          amount: Number(amount.replace(/[^0-9.]/g, "")) || 0,
          stage,
          closeDate: closeDate.toISOString(),
          ownerId: ownerId || undefined,
          accountIds: companyId ? [companyId] : undefined,
        },
        customProperties: Object.keys(customFieldValues).length > 0 ? customFieldValues : undefined,
      });

      if (result.success) {
        const backendId = (result as any).data?.id;
        if (backendId && lineItems.length > 0) {
          if (isLovablePreview()) {
            setProductsForDeal(String(backendId), lineItems);
          } else if (workspaceId) {
            await setDealLineItemsMutation({
              variables: {
                workspaceId,
                dealId: String(backendId),
                lineItems: lineItems.map(li => ({
                  ...(li.productId && { productId: li.productId }),
                  name: li.name,
                  quantity: li.quantity,
                  unitPrice: li.price,
                  listPrice: li.price,
                })),
              },
            });
          }
        }
        onAdd?.({
          company: company.trim(),
          name: dealName.trim(),
          amount: Number(amount.replace(/[^0-9.]/g, "")) || 0,
          stage,
          // NOT localized: this is the row VALUE, re-parsed for sorting and the
          // detail sheet. Only the trigger label below renders in the reader's locale.
          closeDate: format(closeDate, DATE_PATTERN),
          owner,
          customFields: Object.keys(customFieldValues).length > 0 ? customFieldValues : undefined,
          lineItems: lineItems.length > 0 ? lineItems : undefined,
          backendId: backendId ? String(backendId) : undefined,
          // Raw createDeal response node (DealFields + DealRelations) so the grid
          // can show the deal immediately without waiting on the search index.
          backendDeal: (result as any).data,
          // Where the deal actually landed. The caller's view may be filtered to a
          // different pipeline (cloning across pipelines is the common case), so it
          // needs both to move the user there. `stageName` is the display name the
          // Stage chip matches on — `stage` above is the backend externalId.
          pipelineId: selectedPipeline?.id,
          stageName: currentStage?.name || stage,
        });
        reset();
        onOpenChange(false);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => { reset(); onOpenChange(false); };

  const handleCompanyCreated = (newCompany: { name: string; website: string }) => {
    setCompany(newCompany.name);
    const d = newCompany.website?.replace(/^(https?:\/\/)?(www\.)?/, "").split("/")[0] || "";
    setCompanyDomain(d);
    setCompanyConfirmed(true);
    setCompanySearch("");
    setShowAddCompanyForm(false);
  };

  const searchTextForNewCompany = companySearch.trim();
  const hasExactMatch = searchTextForNewCompany
    ? companyResults.some(c => c.name.toLowerCase() === searchTextForNewCompany.toLowerCase())
    : false;

  // Render a built-in field by its standard key
  const renderBuiltInField = (field: typeof sortedFields[0]) => {
    const key = field.standardKey;
    const isRequired = field.required;

    switch (key) {
      case 'company':
        return (
          <div key="company" className="space-y-1.5">
            <Label className="text-sm">{t("fields.deals.company")}{isRequired && <span className="text-destructive ml-0.5">*</span>}</Label>
            {companyConfirmed && company ? (
              <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2">
                <CompanyLogo name={company} domain={companyDomain} />
                <span className="text-sm font-medium flex-1">{company}</span>
                <button
                  onClick={() => { setCompanyConfirmed(false); setCompany(""); setCompanyId(null); setCompanyDomain(""); }}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  {t("forms.actions.change")}
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={company || companySearch}
                  // Opening on focus alone strands the dropdown when this field is
                  // the one Radix autofocuses as the sheet opens: that focus event
                  // fires before the dialog's own open-effect calls reset(), which
                  // clears the flag again. The input keeps focus, so no second focus
                  // event can ever arrive and the typeahead is unreachable until the
                  // user clicks away and back. Field order is workspace config, so
                  // whether Company lands first varies per workspace and pipeline.
                  // Click and typing also open it, which holds whatever has focus.
                  onChange={e => { setCompanySearch(e.target.value); setCompany(""); setShowCompanyDropdown(true); }}
                  onFocus={() => setShowCompanyDropdown(true)}
                  onClick={() => setShowCompanyDropdown(true)}
                  onBlur={() => setTimeout(() => setShowCompanyDropdown(false), 150)}
                  placeholder={t("forms.company.searchPlaceholder")}
                  className="pl-9"
                />
                {showCompanyDropdown && (
                  <div className="absolute z-10 top-full mt-1 w-full border border-border rounded-lg bg-popover shadow-lg max-h-48 overflow-y-auto">
                    {companiesLoading && (
                      <div className="px-3 py-2 text-muted-foreground text-xs flex items-center gap-2">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        {t("forms.actions.searching")}
                      </div>
                    )}
                    {!companiesLoading && !preview && !companySearch.trim() && (
                      <div className="px-3 py-2 text-muted-foreground text-xs">{t("forms.company.typeToSearch")}</div>
                    )}
                    {!companiesLoading && companyResults.map(c => (
                      <button
                        key={c.id}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => { setCompany(c.name); setCompanyId(c.id); setCompanyDomain(c.domain || ""); setCompanyConfirmed(true); setCompanySearch(""); setShowCompanyDropdown(false); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition flex items-center gap-2"
                      >
                        <CompanyLogo name={c.name} domain={c.domain}/>
                        {c.name}
                      </button>
                    ))}
                    <button
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => {
                        setShowCompanyDropdown(false);
                        setShowAddCompanyForm(true);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition flex items-center gap-2 border-t border-border text-accent"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      <span>{searchTextForNewCompany ? t("forms.company.createNewNamed", { name: searchTextForNewCompany }) : t("forms.company.createNew")}</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 'name':
        return (
          <div key="name" className="space-y-1.5">
            <Label htmlFor="deal-name" className="text-sm">{t("fields.deals.name")}{isRequired && <span className="text-destructive ml-0.5">*</span>}</Label>
            <Input id="deal-name" value={dealName} onChange={e => { setDealName(e.target.value); setDealNameTouched(true); }} placeholder={t("forms.deal.namePlaceholder")} />
          </div>
        );

      case 'amount':
        return (
          <div key="amount" className="space-y-4">
            {productsEnabled && (
              <DealProductsPicker
                items={lineItems}
                onChange={setLineItems}
                onTotalChange={(total) => {
                  if (!amountOverridden) {
                    const intPart = String(Math.round(total)).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                    setAmount(intPart);
                  }
                }}
              />
            )}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="deal-amount" className="text-sm">{t("forms.deal.size")}{isRequired && <span className="text-destructive ml-0.5">*</span>}</Label>
                {productsEnabled && lineItems.length > 0 && amountOverridden && (
                  <button
                    type="button"
                    onClick={() => {
                      setAmountOverridden(false);
                      const total = lineItemsTotal(lineItems);
                      const intPart = String(Math.round(total)).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                      setAmount(intPart);
                    }}
                    className="text-xs text-accent hover:underline"
                  >
                    {t("forms.deal.resetToProductTotal")}
                  </button>
                )}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">{currencySymbol}</span>
                <Input id="deal-amount" value={amount} onChange={e => { setAmountOverridden(true); const raw = e.target.value.replace(/[^0-9.]/g, ""); const parts = raw.split("."); const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ","); setAmount(parts.length > 1 ? `${intPart}.${parts[1]}` : intPart); }} placeholder="0" className="pl-9" />
              </div>
              {productsEnabled && lineItems.length > 0 && !amountOverridden && (
                <p className="text-xs text-muted-foreground">{t("forms.deal.autoCalculated")}</p>
              )}
            </div>
          </div>
        );

      case 'stage':
        return (
          <div key="stage" className="space-y-1.5">
            <Label className="text-sm">{t("fields.deals.stage")}{isRequired && <span className="text-destructive ml-0.5">*</span>}</Label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowStageDropdown(!showStageDropdown)}
                onBlur={() => setTimeout(() => setShowStageDropdown(false), 150)}
                className="flex items-center gap-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background hover:bg-muted/50 transition"
              >
                {currentStage && (
                  <div className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: currentStage.color }} />
                )}
                <span className="flex-1 text-left">{currentStage ? stageLabel(currentStage) : stage}</span>
              </button>
              {showStageDropdown && (
                <div className="absolute z-10 top-full mt-1 w-full border border-border rounded-lg bg-popover shadow-lg overflow-hidden">
                  {pipelineStages.map(s => (
                    <button
                      key={s.name}
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => { setStage(getStageValue(s)); setShowStageDropdown(false); }}
                      className={cn(
                        "w-full text-left px-3 py-2 text-sm hover:bg-muted transition flex items-center gap-2",
                        stage === getStageValue(s) && "font-medium text-accent"
                      )}
                    >
                      <div className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                      {stageLabel(s)}
                      <span className="text-xs text-muted-foreground ml-auto">{s.pct}%</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 'close_date':
        return (
          <div key="close_date" className="space-y-1.5">
            <Label className="text-sm">{t("fields.deals.closeDate")}{isRequired && <span className="text-destructive ml-0.5">*</span>}</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !closeDate && "text-muted-foreground")}>
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {closeDate ? displayDate(closeDate) : t("forms.actions.pickDate")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={closeDate} onSelect={d => d && setCloseDate(d)} initialFocus className={cn("p-3 pointer-events-auto")} />
              </PopoverContent>
            </Popover>
          </div>
        );

      case 'owner':
        return (
          <div key="owner" className="space-y-1.5">
            <Label className="text-sm">{t("fields.deals.owner")}{isRequired && <span className="text-destructive ml-0.5">*</span>}</Label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2"><UserAvatar name={owner} size="xs" src={workspaceMembers.find(m => m.name === owner)?.profileImageUrl ?? undefined} /></div>
              <Input
                value={showOwnerDropdown ? ownerSearch : owner}
                // Same autofocus/reset race as the Company field above — Owner is
                // equally reorderable, so it can just as easily be the field that
                // opens focused. Click keeps the current search text; only focus
                // clears it, preserving the existing behaviour on a fresh entry.
                onChange={e => { setOwnerSearch(e.target.value); setShowOwnerDropdown(true); }}
                onFocus={() => { setShowOwnerDropdown(true); setOwnerSearch(""); }}
                onClick={() => setShowOwnerDropdown(true)}
                onBlur={() => setTimeout(() => { setShowOwnerDropdown(false); setOwnerSearch(""); }, 150)}
                placeholder={t("forms.deal.searchOwners")}
                className="pl-11"
              />
              {showOwnerDropdown && filteredOwners.length > 0 && (
                <div className="absolute z-10 top-full mt-1 w-full border border-border rounded-lg bg-popover shadow-lg max-h-36 overflow-y-auto">
                  {filteredOwners.slice(0, 8).map(m => (
                    <button
                      key={m.id}
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => { setOwner(m.name); setOwnerId(m.id); setOwnerSearch(""); setShowOwnerDropdown(false); }}
                      className={cn(
                        "w-full text-left px-3 py-2 text-sm hover:bg-muted transition flex items-center gap-2",
                        owner === m.name && "font-medium text-accent"
                      )}
                    >
                      <UserAvatar name={m.name} size="xs" src={workspaceMembers.find(wm => wm.name === m.name)?.profileImageUrl ?? undefined} />
                      {m.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Render a custom/extra field
  const renderExtraField = (field: typeof sortedFields[0]) => {
    const key = getExtraFieldKey(field);
    const label = getFieldLabel(field);
    const value = customFieldValues[key] || '';
    return (
      <div key={key} className="space-y-1.5">
        <Label className="text-sm">
          {label}
          {field.required && <span className="text-destructive ml-0.5">*</span>}
        </Label>
        <Input
          value={value}
          onChange={e => setCustomFieldValues(prev => ({ ...prev, [key]: e.target.value }))}
          placeholder={t("forms.deal.enterField", { label: label.toLowerCase() })}
        />
      </div>
    );
  };

  // Pipeline selector — rendered automatically before stage when multiple pipelines exist
  const renderPipelineField = () => (
    <div key="pipeline" className="space-y-1.5">
      <Label className="text-sm">{t("fields.deals.pipeline")}<span className="text-destructive ml-0.5">*</span></Label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowPipelineDropdown(!showPipelineDropdown)}
          onBlur={() => setTimeout(() => setShowPipelineDropdown(false), 150)}
          className="flex items-center gap-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background hover:bg-muted/50 transition"
        >
          <span className="flex-1 text-left">{selectedPipeline ? pipelineLabel(selectedPipeline) : t("forms.deal.selectPipeline")}</span>
        </button>
        {showPipelineDropdown && (
          <div className="absolute z-10 top-full mt-1 w-full border border-border rounded-lg bg-popover shadow-lg overflow-hidden">
            {pipelines.map(p => (
              <button
                key={p.id}
                onMouseDown={e => e.preventDefault()}
                onClick={() => {
                  setSelectedPipelineId(p.id);
                  setStage(getStageValue(p.stages[0]));
                  setShowPipelineDropdown(false);
                }}
                className={cn(
                  "w-full text-left px-3 py-2 text-sm hover:bg-muted transition",
                  selectedPipelineId === p.id && "font-medium text-accent"
                )}
              >
                {pipelineLabel(p)}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Render field based on its type
  const renderField = (field: typeof sortedFields[0]) => {
    if (field.kind === 'STANDARD' && BUILT_IN_KEYS.has(field.standardKey || '')) {
      return renderBuiltInField(field);
    }
    return renderExtraField(field);
  };

  const renderFieldsInOrder = () => {
    const elements: React.ReactNode[] = [];
    let hasRenderedBuiltIn = false;
    let hasRenderedCustom = false;
    const rendered = new Set<string>();

    for (const field of sortedFields) {
      const fieldKey = field.kind === 'STANDARD' ? field.standardKey : field.crmFieldId || field.id;
      if (!fieldKey || rendered.has(fieldKey)) continue;

      const isBuiltIn = field.kind === 'STANDARD' && BUILT_IN_KEYS.has(field.standardKey || '');

      // Add separator before first custom field after built-ins
      if (!isBuiltIn && hasRenderedBuiltIn && !hasRenderedCustom) {
        elements.push(<div key="separator" className="border-t border-border" />);
        hasRenderedCustom = true;
      }

      if (isBuiltIn) hasRenderedBuiltIn = true;

      // Render pipeline selector immediately above the stage field.
      if (field.kind === 'STANDARD' && field.standardKey === 'stage') {
        if (hasMultiplePipelines) {
          elements.push(renderPipelineField());
        }
        elements.push(renderBuiltInField(field));
        rendered.add('stage');
        continue;
      }

      // Pair owner + close_date on the same row.
      if (field.kind === 'STANDARD' && (field.standardKey === 'owner' || field.standardKey === 'close_date')) {
        const otherKey = field.standardKey === 'owner' ? 'close_date' : 'owner';
        const otherField = sortedFields.find(f => f.kind === 'STANDARD' && f.standardKey === otherKey);
        if (otherField && !rendered.has(otherKey)) {
          const ownerField = field.standardKey === 'owner' ? field : otherField;
          const closeDateField = field.standardKey === 'close_date' ? field : otherField;
          elements.push(
            <div key="owner-closedate-row" className="grid grid-cols-2 gap-3">
              {renderBuiltInField(ownerField)}
              {renderBuiltInField(closeDateField)}
            </div>
          );
          rendered.add('owner');
          rendered.add('close_date');
          continue;
        }
      }

      elements.push(renderField(field));
      rendered.add(fieldKey);
    }

    return elements;
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) handleCancel(); else onOpenChange(v); }}>
      <SheetContent expandable className="sm:max-w-[50vw] w-full overflow-y-auto p-0 flex flex-col">
        {showAddCompanyForm ? (
          <AddCompanyForm
            onCompanyCreated={handleCompanyCreated}
            onCancel={() => setShowAddCompanyForm(false)}
            initialName={searchTextForNewCompany}
            showBackButton
          />
        ) : (
          <>
            <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
              <SheetTitle className="text-lg font-display">{mode === "clone" ? t("common.cloneEntity", { entity: singular.deals }) : t("common.addEntity", { entity: singular.deals })}</SheetTitle>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
              {fieldsLoading ? (
                <div className="space-y-5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="space-y-1.5">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-10 w-full" />
                    </div>
                  ))}
                </div>
              ) : fieldsError ? (
                <div className="flex flex-col items-center gap-3 py-8 text-center">
                  <AlertCircle className="h-8 w-8 text-destructive" />
                  <div>
                    <p className="text-sm font-medium">{t("forms.deal.fieldsError")}</p>
                    <p className="text-xs text-muted-foreground mt-1 max-w-xs">{fieldsError}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => refetchDealFields()}>
                    {t("forms.actions.retry")}
                  </Button>
                </div>
              ) : fields.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-8 text-center">
                  <AlertCircle className="h-8 w-8 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{t("forms.deal.noFields")}</p>
                    <p className="text-xs text-muted-foreground mt-1">{t("forms.deal.noFieldsDescription")}</p>
                  </div>
                </div>
              ) : (
                renderFieldsInOrder()
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-border px-6 py-4 flex justify-end gap-2">
              <Button variant="outline" onClick={handleCancel}>{t("common.cancel")}</Button>
              <Button onClick={handleSubmit} disabled={!canSubmit || submitting}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {mode === "clone" ? t("common.createClone") : t("common.addEntity", { entity: singular.deals })}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
